import type { FC } from "react";

import { Button } from "src/components";
import { RICH_TEXT_COMPONENTS, Trans, useTranslation } from "src/translations";
import type { WarningConfirmation, WarningConfirmationType } from "src/types";
import { useQuestionnaireWarnings } from "./hooks/useQuestionnaireWarnings";
import { QuestionnaireWarningSection } from "./QuestionnaireWarningSection";

type QuestionnaireResultProps = {
    onContinue: () => void;
} & (
    | { type: "APPROVED" }
    | { type: "REJECTED" }
    | { type: "WARNING"; documentId: string; warningCodes: WarningConfirmationType[]; existingConfirmations: WarningConfirmation[] }
);

const QuestionnaireWarningResult: FC<{
    documentId: string;
    warningCodes: WarningConfirmationType[];
    existingConfirmations: WarningConfirmation[];
    onContinue: () => void;
}> = ({ documentId, warningCodes, existingConfirmations, onContinue }) => {
    const { t } = useTranslation();
    const { sections, confirmSection, confirmingType, allConfirmed } = useQuestionnaireWarnings(
        documentId,
        warningCodes,
        existingConfirmations,
    );

    return (
        <div className="flex flex-col gap-6">
            <p className="text-xl font-semibold">{t("verification.INVESTOR_QUESTIONNAIRE.result.warning.pageTitle")}</p>

            <div className="prose prose-sm">
                <Trans i18nKey="verification.INVESTOR_QUESTIONNAIRE.result.warning.pageText" components={RICH_TEXT_COMPONENTS} />
            </div>

            <div className="flex flex-col gap-4">
                {sections.map((section) => (
                    <QuestionnaireWarningSection
                        key={section.warningType}
                        translationKey={section.translationKey}
                        confirmed={section.confirmed}
                        isConfirming={confirmingType === section.warningType}
                        onConfirm={() => confirmSection(section.warningType)}
                    />
                ))}
            </div>

            <Button type="button" onClick={onContinue} disabled={!allConfirmed} className="self-start">
                {t("verification.INVESTOR_QUESTIONNAIRE.result.warning.continueButtonText")}
            </Button>
        </div>
    );
};

export const QuestionnaireResult: FC<QuestionnaireResultProps> = (props) => {
    const { t } = useTranslation();
    const { type, onContinue } = props;

    if (type === "APPROVED") {
        return (
            <div className="flex flex-col gap-6">
                <p className="text-xl font-semibold">{t("verification.INVESTOR_QUESTIONNAIRE.result.success.pageTitle")}</p>

                <div className="prose prose-sm">
                    <Trans i18nKey="verification.INVESTOR_QUESTIONNAIRE.result.success.pageText" components={RICH_TEXT_COMPONENTS} />
                </div>

                <Button type="button" onClick={onContinue} className="self-start">
                    {t("verification.INVESTOR_QUESTIONNAIRE.result.success.buttonText")}
                </Button>
            </div>
        );
    }

    if (type === "REJECTED") {
        return (
            <div className="flex flex-col gap-6">
                <p className="text-xl font-semibold">{t("verification.INVESTOR_QUESTIONNAIRE.result.rejected.pageTitle")}</p>

                <div className="prose prose-sm">
                    <Trans i18nKey="verification.INVESTOR_QUESTIONNAIRE.result.rejected.pageText" components={RICH_TEXT_COMPONENTS} />
                </div>

                <Button type="button" onClick={onContinue} className="self-start">
                    {t("verification.INVESTOR_QUESTIONNAIRE.result.rejected.buttonText")}
                </Button>
            </div>
        );
    }

    const { documentId, warningCodes, existingConfirmations } = props;

    return (
        <QuestionnaireWarningResult
            documentId={documentId}
            warningCodes={warningCodes}
            existingConfirmations={existingConfirmations}
            onContinue={onContinue}
        />
    );
};
